import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';
import { useMemo } from 'react';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { type CanvasListResponse } from '~/swr-hooks/canvases/types';

export const CANVASES_LIST = 'CANVASES_LIST';

export type CanvasesListKey = [typeof CANVASES_LIST, UseListCanvasesParams];

export const getCanvasesListKey = (params: UseListCanvasesParams) => {
  return [CANVASES_LIST, params];
};

export const isCanvasesListKey = (key: QueryKey): key is CanvasesListKey =>
  Array.isArray(key) && key[0] === CANVASES_LIST && isObject(key[1]);

export type UseListCanvasesParams = Omit<NonNullable<Parameters<typeof Canvases.list>[0]>, 'workspaceId'> & {
  workspaceId: string | undefined;
};

export const useListCanvases = (params: UseListCanvasesParams) => {
  const workspaceId = params.workspaceId;

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useInfiniteQuery({
    queryKey: getCanvasesListKey(params),
    queryFn: workspaceId
      ? async ({ pageParam }) =>
          callAirApi<CanvasListResponse>(() =>
            Canvases.list({
              ...params,
              workspaceId,
              cursor: pageParam || undefined,
            }),
          )
      : skipToken,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
  });

  const flattenedData = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const total = data?.pages[0]?.total ?? 0;

  return {
    data: flattenedData,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    error,
  };
};
